import type { ReactElement, MouseEvent } from 'react';

import { useNavigate, useLocation } from 'react-router-dom';

export type DomEvent = (e: MouseEvent) => void;

export type RouterRenderFunction = (
  arg0: DomEvent,
  arg1?: string | undefined,
) => ReactElement;

type Props = {
  render: RouterRenderFunction;
  onClick?: Function;
  target?: string;
  shouldGoBack?: boolean;
};

type RouteLinkProps = Props & {
  href: string;
};

const isModifiedEvent = (event: MouseEvent) =>
  event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;

// Copy from https://github.com/ReactTraining/react-router/blob/6e9d819dd579715a6cb7502f31bdb25e206c82d6/apps/react-router-dom/modules/Link.js#L18-L34
// We copied the logic from React Router Link in order to provide a component
// that could render navigation links with the specified logic and get it to be reusable
const RouteLink = ({
  href,
  onClick,
  render,
  shouldGoBack = true,
  target,
}: RouteLinkProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (event: MouseEvent) => {
    try {
      if (onClick) onClick(event);
    } catch (ex) {
      event.preventDefault();
      throw ex;
    }
    if (
      !event.defaultPrevented && // onClick prevented default
      event.button === 0 && // ignore everything but left clicks
      (!target || target === '_self') && // let browser handle "target=_blank" etc.
      !isModifiedEvent(event) // ignore clicks with modifier keys
    ) {
      event.preventDefault();
      navigate(
        { ...location, hash: href },
        {
          state: { shouldGoBack },
        },
      );
    }
  };

  return <>{render(handleClick, href)}</>;
};

export default RouteLink;
